// Override of core useHotelsAvailabilityFilters and getPriceFilterOptions (selectors.ts).
// Wraps the core hook and replaces priceRange and hasSetPriceFilterSelector to use
// nightlyDiscountAwareTaxesAndFeesIncluded and priceWithAllDiscounts
// to match the displayed price on hotel cards.
//
// When HotelListOverrideContext provides a priceExtractor (e.g., packages),
// computes price range from extracted prices and disables core's performPriceFilter
// (hasSetPriceFilterSelector = false) so that useHotelList can apply its own filtering.
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useHotelsAvailabilityFilters as useHotelsAvailabilityFiltersBase,
  HotelsAvailabilitySelectors,
  type HotelsState,
} from "@b2bportal/core-hotels";
import { createSelector } from "@reduxjs/toolkit";
import { useHotelListOverride } from "../../context/HotelListOverrideContext";

const initialPriceRange = { max: Number.MAX_SAFE_INTEGER, min: 0 };

const getPriceFilterOptions = createSelector(
  HotelsAvailabilitySelectors.getHotels,
  (hotels) => {
    const prices = hotels.flatMap((hotel) => {
      const { price } = hotel;
      if (price == null) return [];
      const nightlyDA =
        price.nightlyDiscountAwareTaxesAndFeesIncluded ??
        price.nightlyDiscountAware;
      return [
        (
          nightlyDA.priceWithAllDiscounts ??
          nightlyDA.priceWithUnmanagedDiscounts
        ).fiat,
      ];
    });

    if (prices.length === 0) return undefined;

    return prices.reduce(
      (carry, item) => ({
        min: { ...carry.min, value: Math.min(item.value, carry.min.value) },
        max: { ...carry.max, value: Math.max(item.value, carry.max.value) },
      }),
      { min: prices[0], max: prices[0] }
    );
  }
);

export const useHotelsAvailabilityFilters = () => {
  const delegated = useHotelsAvailabilityFiltersBase();
  const override = useHotelListOverride();

  const standalonePriceRange = useSelector(getPriceFilterOptions);
  const priceFilter = useSelector(HotelsAvailabilitySelectors.getPriceFilter);
  const hotels = useSelector(HotelsAvailabilitySelectors.getHotels);

  // When a priceExtractor is provided, compute price range from extracted prices
  const overridePriceRange = useMemo(() => {
    if (!override?.priceExtractor) return undefined;

    const prices = hotels
      .map((hotel) => override.priceExtractor!(hotel))
      .filter((price): price is number => price != null);

    if (prices.length === 0) return undefined;

    const min = Math.min(...prices);
    const max = Math.max(...prices);

    // Reuse fiat shape from standalone range for currency info
    const templateFiat = standalonePriceRange?.min ?? {
      value: 0,
      currencyCode: "CAD",
      currencySymbol: "$",
    };

    return {
      min: { ...templateFiat, value: min },
      max: { ...templateFiat, value: max },
    };
  }, [override, hotels, standalonePriceRange]);

  const priceRange = overridePriceRange ?? standalonePriceRange;

  const hasSetPriceFilterSelector = useMemo(() => {
    // When priceExtractor is active, disable core's performPriceFilter
    // (useHotelList handles price filtering with the extracted prices)
    if (override?.priceExtractor) return false;

    return (
      (priceFilter?.max !== priceRange?.max.value &&
        priceFilter?.max !== initialPriceRange.max) ||
      (priceFilter?.min !== priceRange?.min.value &&
        priceFilter?.min !== initialPriceRange.min)
    );
  }, [override, priceFilter, priceRange]);

  return useMemo(
    () => ({
      ...delegated,
      context: {
        ...delegated.context,
        hasSetPriceFilterSelector,
        filterOptions: {
          ...delegated.context.filterOptions,
          priceRange,
        },
      },
    }),
    [delegated, hasSetPriceFilterSelector, priceRange]
  );
};
